import { useEffect, useRef, useState } from 'react';
import { useAppSelector } from '@/hooks/useAppSelector';
import { usePermission } from '@/hooks/usePermission';
import { Permissions } from '@/constants/permissions';
import type { SessionContext } from '@/state/sessionSlice';

/** The login-context filters a transaction list applies (year, location, cost centre). */
export interface ContextScope {
  financialYearCode?: string;
  locationId?:        string;
  costCenterId?:      string;
}

export type ScopeKey = keyof ContextScope;

export interface ScopeChip {
  key:      ScopeKey;
  label:    string;
  /** The user may not look outside this scope (no "view all" permission). */
  locked:   boolean;
  onClear?: () => void;
}

const ALL_KEYS: readonly ScopeKey[] = ['financialYearCode', 'locationId', 'costCenterId'];

const VIEW_ALL: Record<ScopeKey, string> = {
  financialYearCode: Permissions.TXN_VIEW_ALL_FY,
  locationId:        Permissions.TXN_VIEW_ALL_LOCATIONS,
  costCenterId:      Permissions.TXN_VIEW_ALL_COST_CENTERS,
};

/** The scope the login context implies, limited to the keys a list supports. */
export const scopeFromSession = (
  session: SessionContext | null | undefined,
  keys: readonly ScopeKey[] = ALL_KEYS,
): ContextScope => {
  const s: ContextScope = {};
  for (const k of keys) {
    const v = session?.[k];
    if (v) s[k] = v;
  }
  return s;
};

const sameScope = (a: ContextScope, b: ContextScope, keys: readonly ScopeKey[]) =>
  keys.every(k => a[k] === b[k]);

const chipLabel = (session: SessionContext | null, key: ScopeKey): string => {
  if (!session) return '';
  switch (key) {
    case 'financialYearCode': return session.financialYearCode ?? '';
    case 'locationId':
      return session.locationCode ? `${session.locationCode} — ${session.locationName ?? ''}` : session.locationName ?? '';
    case 'costCenterId':
      return session.costCenterCode ? `${session.costCenterCode} — ${session.costCenterName ?? ''}` : session.costCenterName ?? '';
  }
};

/**
 * The list's scope filters, starting from the login context. Chips reflect what is actually applied:
 * clearing one removes it from the list AND from the chips; `reset` restores the login context.
 * Switching the login context (top bar) restarts from it. `apply` receives the new scope — merge it
 * into the list filter (and go back to page 1). Initialise the filter with `scopeFromSession`.
 */
export function useContextScope(apply: (scope: ContextScope) => void, keys: readonly ScopeKey[] = ALL_KEYS) {
  const session = useAppSelector(s => s.session.session);
  const { hasPermission } = usePermission();
  const [scope, setScope] = useState<ContextScope>(() => scopeFromSession(session, keys));

  const applyRef = useRef(apply);
  applyRef.current = apply;
  const change = (next: ContextScope) => {
    // Every supported key, cleared ones as undefined, so merging it into the filter removes them.
    const full = Object.fromEntries(keys.map(k => [k, next[k]])) as ContextScope;
    setScope(full);
    applyRef.current(full);
  };

  const prevSession = useRef(session);
  useEffect(() => {
    const prev = prevSession.current;
    prevSession.current = session;
    if (!session || prev === session) return;
    const next = scopeFromSession(session, keys);
    if (!sameScope(next, scopeFromSession(prev, keys), keys)) change(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session]);

  const chips: ScopeChip[] = keys
    .filter(k => scope[k])
    .map(k => {
      const canClear = hasPermission(VIEW_ALL[k]);
      return {
        key:     k,
        label:   chipLabel(session, k),
        locked:  !canClear,
        onClear: canClear ? () => change({ ...scope, [k]: undefined }) : undefined,
      };
    });

  const home = scopeFromSession(session, keys);
  return {
    chips,
    /** True when the user has cleared something the login context would apply. */
    canReset: !sameScope(scope, home, keys),
    reset:    () => change(home),
  };
}
